import React from 'react';
import type { ButtonProps, ButtonColor } from './Button.types';
import { cva } from 'class-variance-authority';

// Using a simple cx function instead of an external utility
const cx = (...inputs: (string | undefined | null | false)[]) => {
  return inputs.filter(Boolean).join(' ');
};

const buttonVariants = cva(
  [
    'inline-flex items-center justify-center rounded-md font-medium whitespace-nowrap transition-all duration-150 cursor-pointer',
    'focus:outline-hidden focus:ring-4 focus:ring-offset-2',
    'disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none'
  ],
  {
    variants: {
      type: {
        default: 'shadow-inner hover:brightness-110 dark:hover:brightness-90',
        outline: 'bg-transparent border shadow-xs hover:brightness-98 dark:hover:brightness-102',
        plain: 'bg-transparent border-transparent hover:brightness-98 dark:hover:brightness-102'
      },
      color: {
        primary: '',
        secondary: '',
        tertiary: '',
      },
      size: {
        xs: '',
        sm: '',
        base: '',
        l: '',
        xl: '',
      },
      iconOnly: {
        true: '',
        false: ''
      },
      fullWidth: {
        true: 'w-full',
        false: ''
      }
    },
    compoundVariants: [
      // Primary
      { type: 'default',
        color: 'primary',
        // El `!` (important) en el fondo evita que un reset SIN capa del consumidor
        // (p. ej. `button { background-color: transparent }`) gane a esta utilidad,
        // que se emite dentro de `@layer utilities`. Sin él, el botón sale
        // blanco-sobre-blanco en apps reales. Ver SBC-3886.
        className: 'bg-primary-700! dark:bg-primary-200! text-primary-50 dark:text-primary-800 border-primary-600 dark:border-primary-300 focus:ring-primary-400 focus:ring-offset-primary-100 dark:focus:ring-primary-500/50 dark:focus:ring-offset-gray-900'
      },
      {
        type: 'outline',
        color: 'primary',
        className: 'text-primary-700 dark:text-primary-400 border-primary-300 dark:border-primary-700 hover:bg-primary-100 dark:hover:bg-primary-950 focus:ring-primary-400 focus:ring-offset-primary-100 dark:focus:ring-primary-500/50 dark:focus:ring-offset-gray-900'
      },
      {
        type: 'plain',
        color: 'primary',
        className: 'text-primary-700 dark:text-primary-400 hover:bg-primary-100 dark:hover:bg-primary-950 hover:border-primary-100 dark:hover:border-primary-950 focus:ring-primary-400 focus:ring-offset-primary-100 dark:focus:ring-primary-500/50 dark:focus:ring-offset-gray-900'
      },

      // Secondary
      {
        type: 'default',
        color: 'secondary',
        className: 'bg-secondary-950! dark:bg-secondary-400! text-secondary-50 dark:text-secondary-950 border-secondary-950 dark:border-secondary-400 focus:ring-secondary-400 focus:ring-offset-secondary-100 dark:focus:ring-secondary-500/50 dark:focus:ring-offset-gray-900' },
      {
        type: 'outline',
        color: 'secondary',
        className: 'text-secondary-950 dark:text-secondary-300 border-secondary-300 dark:border-secondary-700 hover:bg-secondary-100 dark:hover:bg-secondary-900 focus:ring-secondary-400 focus:ring-offset-secondary-100 dark:focus:ring-secondary-500/50 dark:focus:ring-offset-gray-900'
      },
      {
        type: 'plain',
        color: 'secondary',
        className: 'text-secondary-950 dark:text-secondary-300 hover:bg-secondary-100 dark:hover:bg-secondary-900 hover:border-secondary-100 dark:hover:border-secondary-900 focus:ring-secondary-400 focus:ring-offset-secondary-100 dark:focus:ring-secondary-500/50 dark:focus:ring-offset-gray-900'
      },

      // Tertiary
      {
        type: 'default',
        color: 'tertiary',
        className: 'bg-tertiary-800! dark:bg-tertiary-400! text-tertiary-50 dark:text-tertiary-800 border-tertiary-800 dark:border-tertiary-800 focus:ring-tertiary-400 focus:ring-offset-tertiary-100 dark:focus:ring-tertiary-500/50 dark:focus:ring-offset-gray-900' },
      {
        type: 'outline',
        color: 'tertiary',
        className: 'text-tertiary-800 dark:text-tertiary-300 border-tertiary-300 dark:border-tertiary-700 hover:bg-tertiary-100 dark:hover:bg-tertiary-800 focus:ring-tertiary-400 focus:ring-offset-tertiary-100 dark:focus:ring-tertiary-500/50 dark:focus:ring-offset-gray-900' },
      {
        type: 'plain',
        color: 'tertiary',
        className: 'text-tertiary-800 dark:text-tertiary-300 hover:bg-tertiary-100 dark:hover:bg-tertiary-800 hover:border-tertiary-100 dark:hover:border-tertiary-800 focus:ring-tertiary-400 focus:ring-offset-tertiary-100 dark:focus:ring-tertiary-500/50 dark:focus:ring-offset-gray-900' },

      // Sizes
      { size: 'xs', iconOnly: false, className: 'h-7 px-2 gap-2 text-xs' },
      { size: 'sm', iconOnly: false, className: 'h-8 px-2 gap-2 text-sm' },
      { size: 'base', iconOnly: false, className: 'h-9 px-3 gap-2 text-base' },
      { size: 'l', iconOnly: false, className: 'h-10 px-3 gap-3 text-lg' },
      { size: 'xl', iconOnly: false, className: 'h-11 px-4 gap-3 text-xl' },

      // Sizes for iconOnly
      { size: 'xs', iconOnly: true, className: 'h-7 w-7' },
      { size: 'sm', iconOnly: true, className: 'h-8 w-8' },
      { size: 'base', iconOnly: true, className: 'h-9 w-9' },
      { size: 'l', iconOnly: true, className: 'h-10 w-10' },
      { size: 'xl', iconOnly: true, className: 'h-11 w-11' },
    ],
    defaultVariants: {
      type: 'default',
      color: 'primary',
      size: 'base',
      iconOnly: false,
      fullWidth: false
    }
  }
);

const iconSizeClasses = {
  xs: 'w-4 h-4 shrink-0',
  sm: 'w-4 h-4 shrink-0',
  base: 'w-4 h-4 shrink-0',
  l: 'w-4 h-4 shrink-0',
  xl: 'w-4 h-4 shrink-0',
};

export const Button: React.FC<ButtonProps> = ({
  type = 'default',
  color = 'primary',
  size = 'base',
  iconOnly = false,
  leftIcon,
  rightIcon,
  children,
  disabled = false,
  className = '',
  onClick,
  htmlType = 'button',
  fullWidth = false,
  ariaLabel,
  badge = false,
  badgeCount,
  ...rest
}) => {
  const renderIcon = (icon: React.ReactNode) => {
    if (!icon) return null;
    return (
      <span className={`inline-flex items-center justify-center shrink-0 ${iconSizeClasses[size]}`}>
        {icon}
      </span>
    );
  };

  const renderContent = () => {
    if (iconOnly) {
      return renderIcon(leftIcon);
    }

    return (
      <>
        {leftIcon && renderIcon(leftIcon)}
        {children && <span className="truncate">{children}</span>}
        {rightIcon && renderIcon(rightIcon)}
      </>
    );
  };

  const renderBadge = () => {
    if (!badge && badgeCount === undefined) return null;

    if (badgeCount !== undefined) {
      return (
        <span
          className={cx(
            'absolute -top-1 -right-1 flex items-center justify-center',
            'min-w-[16px] h-4 px-1 rounded-full text-[10px] font-medium leading-none',
            'pointer-events-none',
            'bg-red-700 dark:bg-red-100 text-red-50 dark:text-red-700'
          )}
          aria-label={`${badgeCount} notificaciones`}
        >
          {badgeCount > 99 ? '99+' : badgeCount}
        </span>
      );
    }

    return (
      <span
        className={cx(
          'absolute -top-1 -right-1 w-2 h-2 rounded-full pointer-events-none',
          'bg-red-700 dark:bg-red-100'
        )}
        aria-label="Notificación"
      />
    );
  };

  return (
    <button
      type={htmlType}
      className={cx(
        buttonVariants({
          type,
          color,
          size,
          iconOnly,
          fullWidth
        }),
        (badge || badgeCount !== undefined) ? 'relative' : false,
        className
      )}
      disabled={disabled}
      onClick={onClick}
      aria-label={ariaLabel}
      {...rest}
    >
      {renderContent()}
      {renderBadge()}
    </button>
  );
};